/**
 * Large UPS draft/name/clone bindings for the descriptor engine (#997 Story 6.5,
 * mirror of `smallUpsEngineBindings.ts`).
 *
 * The engine's generic factories (`descriptorCreateDraft` /
 * `descriptorEnsureNameForCreate` / `descriptorCloneForCreate`) don't match the
 * bespoke Large UPS CREATE-flow semantics: bespoke drafts carry
 * `DEFAULT_LARGE_UPS_CONFIGURATION` (status pre-filled to 'new'), auto-names are
 * serial hashes (`generateAutoLargeUpsName`), and duplicates take a fresh name
 * while dropping id/metadata. Per the story ADR (parity by reuse), each binding
 * here delegates to the PROVEN bespoke util and only converts shapes at the
 * engine boundary (`LargeUpsConfiguration` ↔ `DescriptorInstance` via the Story
 * 6.5 converters).
 */

import {
  cloneLargeUpsForCreate,
  createDraftLargeUpsConfiguration,
} from '../../../../components/largeups/largeUpsListUtils';
import {
  descriptorInstanceToLargeUps,
  largeUpsToDescriptorInstance,
} from '../../../../services/descriptorEditSessionFlush';
import { generateAutoLargeUpsName } from '../../../../utils/largeUpsAutoName';
import { LARGE_UPS_DESCRIPTOR } from '../../../sections/largeUps.descriptor';

import type { UpdateLargeUpsConfigurationInput } from '../../../../types/largeUps';
import type {
  DescriptorInstance,
  DescriptorInstancePatch,
} from '../../../sections/descriptorEngineAdapter';

function patchToLargeUpsInput(
  data: DescriptorInstancePatch,
): UpdateLargeUpsConfigurationInput {
  const { instanceName, instanceStatus, ...fields } = data;
  return {
    ...fields,
    name: typeof instanceName === 'string' ? instanceName : undefined,
    status: instanceStatus as UpdateLargeUpsConfigurationInput['status'],
  } as UpdateLargeUpsConfigurationInput;
}

/** Bespoke draft factory: DEFAULT_LARGE_UPS_CONFIGURATION + normalizeStatus. */
export function largeUpsCreateDraft(
  data: DescriptorInstancePatch,
  displayOrder: number,
  tempId: number,
): DescriptorInstance {
  const unit = createDraftLargeUpsConfiguration(
    patchToLargeUpsInput(data),
    displayOrder,
    tempId,
  );
  return largeUpsToDescriptorInstance(unit) as DescriptorInstance;
}

/** Bespoke auto-name: serial hash, mirrors ensureLargeUpsNameForCreate. */
export function largeUpsEnsureNameForCreate(
  data: DescriptorInstancePatch,
  tempId: number,
  takenNames: Iterable<string | undefined>,
): DescriptorInstancePatch {
  if (typeof data.instanceName === 'string' && data.instanceName.trim()) {
    return data;
  }
  return {
    ...data,
    instanceName: generateAutoLargeUpsName(tempId, takenNames),
  };
}

/** Bespoke duplicate: fresh name, keeps status, drops id/metadata. */
export function largeUpsCloneForCreate(
  source: DescriptorInstance,
  takenNames: Iterable<string | undefined>,
  tempId: number,
): Partial<DescriptorInstance> {
  const sourceUnit = descriptorInstanceToLargeUps(
    source,
    0,
    LARGE_UPS_DESCRIPTOR,
  );
  const cloned = cloneLargeUpsForCreate(sourceUnit, takenNames, tempId);
  return largeUpsToDescriptorInstance({
    ...cloned,
    id: tempId,
  } as Parameters<typeof largeUpsToDescriptorInstance>[0]) as Partial<DescriptorInstance>;
}
